import { useState, useEffect, Children } from 'react'
import { useAuth } from '../../../context/AuthContext'
import { useWedding } from '../../../context/WeddingContext'
import { supabase } from '../../../lib/supabase'
import { compressImage } from '../../../lib/imageCompression'
import { useNavigate } from 'react-router-dom'
import { Check, Crown, LogOut, User, Copy, ExternalLink, Trash2, Send, Lock, ChevronDown, ChevronUp, Search, X } from 'lucide-react'

// Guest portal hero presets (must mirror HERO_PRESETS in WeddingMiniSite.jsx)
const SETTINGS_HERO_PRESETS = [
  { key:'floral',  label:'Floral',  swatch:'linear-gradient(135deg,#F4D9CE,#E7B9A6)' },
  { key:'blush',   label:'Blush',   swatch:'linear-gradient(135deg,#F6E2DA,#EBC7B8)' },
  { key:'sage',    label:'Sage',    swatch:'linear-gradient(135deg,#E4EBE0,#C7D6C0)' },
  { key:'classic', label:'Classic', swatch:'linear-gradient(135deg,#F5EDE0,#EDE0D4)' },
]

// ── Helpers ───────────────────────────────────────────────
function getWeddingUrl(slug) {
  const base = import.meta.env.VITE_APP_URL || window.location.origin
  return `${base}/w/${slug}`
}
function isAllCaps(str) { return str.length > 3 && str === str.toUpperCase() && /[A-Z]/.test(str) }
function autoCapitalize(str) { return str.replace(/\b\w/g, c => c.toUpperCase()) }


// ── Settings search map ───────────────────────────────────
const SETTINGS_ITEMS = [
  { label:'Full Name',               section:'Your Account',    sectionId:'settings-account',   keywords:['name','profile','full name','display name'] },
  { label:'Email Address',           section:'Your Account',    sectionId:'settings-account',   keywords:['email','account','login'] },
  { label:'Current Plan / Upgrade',  section:'Your Account',    sectionId:'settings-account',   keywords:['plan','upgrade','premium','free','subscription'] },
  { label:'Wedding Date',            section:'Your Wedding',    sectionId:'settings-wedding',   keywords:['date','wedding date','countdown','change date'] },
  { label:'Wedding Link & Slug',     section:'Your Wedding',    sectionId:'settings-wedding',   keywords:['link','url','share','portal','slug','customize link'] },
  { label:'Guest Portal Tabs',       section:'Your Wedding',    sectionId:'settings-wedding',   keywords:['portal','visibility','hide','show','tabs','guests see'] },
  { label:'Password Protection',     section:'Your Wedding',    sectionId:'settings-wedding',   keywords:['password','private','protect','lock portal'] },
  { label:'Currency',                section:'Currency',        sectionId:'settings-currency',  keywords:['currency','naira','ngn','pound','dollar','euro','cedi'] },
  { label:'Invite Your Partner',     section:'People & Access', sectionId:'settings-people',    keywords:['partner','invite','spouse','access','couple'] },
  { label:'Check-In Coordinator',    section:'People & Access', sectionId:'settings-people',    keywords:['coordinator','check-in','checkin','usher','access'] },
  { label:'Aso Ebi Ambassadors',     section:'People & Access', sectionId:'settings-people',    keywords:['ambassador','aso ebi','fabric','representative'] },
  { label:'RSVP Notifications',      section:'Notifications',   sectionId:'settings-notifs',    keywords:['rsvp','notification','alert','response'] },
  { label:'Guest Check-In Alerts',   section:'Notifications',   sectionId:'settings-notifs',    keywords:['check-in','arrival','notification','alert'] },
  { label:'Aso Ebi Order Alerts',    section:'Notifications',   sectionId:'settings-notifs',    keywords:['aso ebi','order','notification','alert'] },
  { label:'Registry Notifications',  section:'Notifications',   sectionId:'settings-notifs',    keywords:['registry','gift','notification','alert'] },
  { label:'Weekly Reminder',         section:'Notifications',   sectionId:'settings-notifs',    keywords:['weekly','reminder','planning','tasks'] },
  { label:'Sign Out',                section:'Account Actions', sectionId:'settings-actions',   keywords:['sign out','logout','log out','exit'] },
  { label:'Delete Account',          section:'Account Actions', sectionId:'settings-actions',   keywords:['delete','remove','account','data'] },
]

// ── Section accordion ─────────────────────────────────────
function SettingsSection({ title, defaultOpen=true, id, forceOpen, children }) {
  const [open, setOpen] = useState(defaultOpen)
  useEffect(() => { if (forceOpen) setOpen(true) }, [forceOpen])
  return (
    <div id={id} style={{ border:'1px solid #E8E0D8', borderRadius:'1rem', overflow:'hidden', background:'white' }}>
      <button onClick={()=>setOpen(o=>!o)} style={{ width:'100%', display:'flex', alignItems:'center', justifyContent:'space-between', padding:'1rem 1.25rem', background:'none', border:'none', cursor:'pointer', textAlign:'left' }}>
        <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', fontWeight:700, color:'#8C7B70', letterSpacing:'0.12em', textTransform:'uppercase' }}>{title}</span>
        {open ? <ChevronUp size={14} color="#BCBCBC"/> : <ChevronDown size={14} color="#BCBCBC"/>}
      </button>
      {open && (
        <div style={{ borderTop:'1px solid #F3F0EA', padding:'1.25rem' }}>
          {Children.toArray(children).filter(Boolean).map((child, i) => (
            <div key={i}>
              {i > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:'0.6rem', margin:'1.4rem 0' }}>
                  <div style={{ flex:1, height:1, background:'linear-gradient(to right, transparent, #EBE2D8)' }}/>
                  <span style={{ fontSize:'0.62rem', color:'#D8C7B6', lineHeight:1 }}>❀</span>
                  <div style={{ flex:1, height:1, background:'linear-gradient(to left, transparent, #EBE2D8)' }}/>
                </div>
              )}
              {child}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Sub-section (collapsible advanced options) ────────────
function AdvancedToggle({ label, children }) {
  const [open, setOpen] = useState(false)
  return (
    <div>
      <button onClick={()=>setOpen(o=>!o)} style={{ display:'flex', alignItems:'center', gap:'0.4rem', background:'none', border:'none', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', fontWeight:600, color:'#B36B55', padding:0 }}>
        {open?<ChevronUp size={12}/>:<ChevronDown size={12}/>} {label}
      </button>
      {open && <div style={{ marginTop:'0.75rem', paddingLeft:'0.75rem', borderLeft:'2px solid #F0EBE3' }}>{children}</div>}
    </div>
  )
}

// ── Toggle ────────────────────────────────────────────────
function Toggle({ on, onToggle }) {
  return (
    <button onClick={onToggle} style={{ width:44, height:24, borderRadius:100, background:on?'#B36B55':'#D1C9C0', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:3, left:on?23:3, transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
    </button>
  )
}

// ── Delete Modal ──────────────────────────────────────────
function DeleteModal({ onClose, onDelete, deleting }) {
  const [step, setStep]     = useState(1)
  const [text, setText]     = useState('')
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div className="card" style={{ width:'100%', maxWidth:420, padding:'1.5rem' }}>
        {step===1 ? (
          <>
            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', color:'#1A1A1A', margin:'0 0 0.75rem' }}>Delete your account?</h3>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', color:'#6B6B6B', margin:'0 0 0.75rem', lineHeight:1.6 }}>This will permanently delete:</p>
            {['Your wedding profile and all events','Your complete guest list','All registry items and gift logs','All photos and memories','Your Aso Ebi records'].map(item=>(
              <div key={item} style={{ display:'flex', gap:'0.5rem', marginBottom:'0.3rem' }}>
                <span style={{ color:'#DC2626', flexShrink:0 }}>·</span>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#4A3728' }}>{item}</span>
              </div>
            ))}
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', fontWeight:700, color:'#DC2626', margin:'0.875rem 0' }}>This cannot be undone.</p>
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <button onClick={onClose} className="btn-secondary" style={{ flex:1 }}>Cancel</button>
              <button onClick={()=>setStep(2)} style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:700, color:'#DC2626', background:'rgba(220,38,38,0.06)', border:'1px solid rgba(220,38,38,0.2)', borderRadius:100, padding:'0.65rem', cursor:'pointer' }}>Continue →</button>
            </div>
          </>
        ) : (
          <>
            <h3 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.1rem', color:'#DC2626', margin:'0 0 0.75rem' }}>Type DELETE to confirm</h3>
            <input className="input-field" placeholder="Type DELETE here" value={text} onChange={e=>setText(e.target.value)} style={{ marginBottom:'1rem' }} autoFocus/>
            <div style={{ display:'flex', gap:'0.5rem' }}>
              <button onClick={onClose} className="btn-secondary" style={{ flex:1 }}>Cancel</button>
              <button onClick={onDelete} disabled={text!=='DELETE'||deleting}
                style={{ flex:1, fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:700, color:'white', background:text==='DELETE'?'#DC2626':'#BCBCBC', border:'none', borderRadius:100, padding:'0.65rem', cursor:text==='DELETE'?'pointer':'not-allowed', transition:'background 0.2s' }}>
                {deleting?'Deleting…':'Delete Everything'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// ── Main ─────────────────────────────────────────────────
export default function SettingsPage() {
  const {user, profile, signOut, refreshProfile } = useAuth()
  const { wedding, refreshWedding , isPremium } = useWedding()
  const navigate = useNavigate()

  const lbl = { fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#6B6B6B', textTransform:'uppercase', letterSpacing:'0.04em', display:'block', marginBottom:'0.3rem' }

  // Profile
  const [fullName, setFullName]         = useState('')
  const [nameErr, setNameErr]           = useState('')
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileSaved, setProfileSaved] = useState(false)

  // Wedding link / slug
  const [editSlug, setEditSlug]         = useState('')
  const [slugError, setSlugError]       = useState('')
  const [slugSaving, setSlugSaving]     = useState(false)
  const [slugSaved, setSlugSaved]       = useState(false)
  const [copied, setCopied]             = useState(false)

  // Portal visibility — keys match actual guest portal tab IDs
  const defaultVisibility = { story:true, events:true, registry:true, photos:true, programme:true, eyes:true, asoebi:true }
  const [visibility, setVisibility]     = useState(defaultVisibility)

  // Guest portal hero
  const [heroPreset, setHeroPreset]     = useState('floral')
  const [heroImageUrl, setHeroImageUrl] = useState('')
  const [heroTagline, setHeroTagline]   = useState('')
  const [heroTagSaved, setHeroTagSaved] = useState(false)
  const [heroShowCarousel, setHeroShowCarousel] = useState(false)
  const [uploadingHero, setUploadingHero] = useState(false)
  const [heroSaved, setHeroSaved]       = useState(false)

  // Password protection
  const [passwordProtected, setPasswordProtected] = useState(false)
  const [portalPassword, setPortalPassword]       = useState('')
  const [passwordSaved, setPasswordSaved]         = useState(false)
  const [passwordErr, setPasswordErr]             = useState('')

  // Bank / Paystack

  // Currency
  const [currencyCode, setCurrencyCode] = useState('NGN')
  const [currencySaved, setCurrencySaved] = useState(false)

  // Partner
  const [partnerPhone, setPartnerPhone]   = useState('')
  const [inviteSent, setInviteSent]     = useState(false)
  const [inviteSending, setInviteSending] = useState(false)

  // Wedding Date
  const [weddingDate, setWeddingDate] = useState('')
  const [dateSaved, setDateSaved]     = useState(false)
  const [savingDate, setSavingDate]   = useState(false)

  // Search
  const [searchQuery, setSearchQuery]       = useState('')
  const [showSearch, setShowSearch]         = useState(false)
  const [activeSectionId, setActiveSectionId] = useState(null)

  function goToSection(sectionId) {
    setActiveSectionId(sectionId)
    setSearchQuery(''); setShowSearch(false)
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior:'smooth', block:'start' })
    }, 80)
  }

  // PIN generator
  function genPin() { return String(Math.floor(1000 + Math.random() * 9000)) }

  // Coordinator
  const [coordName, setCoordName]       = useState('')
  const [coordPhone, setCoordPhone]     = useState('')
  const [coordSent, setCoordSent]       = useState(false)

  // Ambassadors
  const [ambName, setAmbName]       = useState('')
  const [ambPhone, setAmbPhone]     = useState('')
  const [ambassadors, setAmbassadors] = useState([])
  const [ambSaving, setAmbSaving]   = useState(false)

  // Notifications
  const [notifPrefs, setNotifPrefs] = useState({ rsvp:{on:true}, checkin:{on:true}, asoebi:{on:true}, registry:{on:true}, reminders:{on:true} })
  const [notifSaved, setNotifSaved] = useState(false)

  // Story feature consent
  const [storyFeatured,  setStoryFeatured]  = useState(false)
  const [storyNameMode,  setStoryNameMode]  = useState('real')
  const [storySaving,    setStorySaving]    = useState(false)
  const [storySaved,     setStorySaved]     = useState(false)

  // Delete
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting]     = useState(false)

  useEffect(()=>{
    if(!wedding) return
    if(wedding.currency) setCurrencyCode(wedding.currency||'NGN')
    if(wedding.slug) setEditSlug(wedding.slug)
    if(wedding.wedding_date) setWeddingDate(wedding.wedding_date.slice(0,10))
    if(wedding.portal_visibility) setVisibility({...defaultVisibility,...wedding.portal_visibility})
    if(wedding.hero_preset) setHeroPreset(wedding.hero_preset)
    if(wedding.hero_image_url) setHeroImageUrl(wedding.hero_image_url)
    if(wedding.hero_tagline != null) setHeroTagline(wedding.hero_tagline)
    if(wedding.hero_show_carousel != null) setHeroShowCarousel(!!wedding.hero_show_carousel)
    if(wedding.portal_password_protected) setPasswordProtected(true)
    if(wedding.checkin_attendant_name) setCoordName(wedding.checkin_attendant_name)
    if(wedding.checkin_attendant_phone) setCoordPhone(wedding.checkin_attendant_phone)
    if(wedding.notification_prefs) setNotifPrefs(p=>({...p,...wedding.notification_prefs}))
    if(wedding.story_featured != null) setStoryFeatured(!!wedding.story_featured)
    if(wedding.story_featured_name_mode) setStoryNameMode(wedding.story_featured_name_mode)
    // Load ambassadors
    supabase.from('asoebi_ambassadors').select('*').eq('wedding_id',wedding.id)
      .then(({data})=>{ if(data) setAmbassadors(data) })
  },[wedding])


  useEffect(()=>{ if(profile?.full_name) setFullName(profile.full_name) },[profile])

  if(!wedding) return <div style={{textAlign:'center',padding:'3rem'}}><p style={{fontFamily:'Nunito Sans, sans-serif',color:'#BCBCBC'}}>Loading…</p></div>

  const weddingUrl  = getWeddingUrl(wedding.slug)
  const isLocalhost = window.location.hostname==='localhost'

  // ── Saves ──────────────────────────────────────────────
  async function saveProfile() {
    const words = fullName.trim().split(/\s+/)
    if(words.length<2){ setNameErr('Please enter your first and last name'); return }
    if(fullName.trim().length<4){ setNameErr('Name too short'); return }
    if(isAllCaps(fullName)){ setNameErr('Please use standard capitalization'); return }
    setSavingProfile(true); setNameErr('')
    await supabase.from('profiles').update({full_name:fullName.trim()}).eq('id',user.id)
    await refreshProfile()
    setProfileSaved(true); setTimeout(()=>setProfileSaved(false),2000)
    setSavingProfile(false)
  }

  async function saveWeddingDate() {
    if (!weddingDate || !wedding) return
    setSavingDate(true)
    await supabase.from('weddings').update({ wedding_date: weddingDate }).eq('id', wedding.id)
    await refreshWedding()
    setDateSaved(true); setTimeout(()=>setDateSaved(false), 2000)
    setSavingDate(false)
  }

  async function saveSlug() {
    const slug = editSlug.trim().toLowerCase()
    if(!/^[a-z0-9-]{5,}$/.test(slug)){ setSlugError('Lowercase letters, numbers and hyphens only, min 5 characters'); return }
    if(slug===wedding.slug){ setSlugSaved(true); setTimeout(()=>setSlugSaved(false),2000); return }
    setSlugSaving(true); setSlugError('')
    const { data:taken } = await supabase.rpc('is_slug_taken', { p_slug: slug })
    if(taken){ setSlugError('This link is taken — try another'); setSlugSaving(false); return }
    await supabase.from('weddings').update({slug}).eq('id',wedding.id)
    await refreshWedding()
    setSlugSaved(true); setTimeout(()=>setSlugSaved(false),2000)
    setSlugSaving(false)
  }

  // ── Guest portal hero ──────────────────────────────────
  async function selectHeroPreset(key) {
    setHeroPreset(key)
    await supabase.from('weddings').update({ hero_preset: key }).eq('id', wedding.id)
    await refreshWedding()
    setHeroSaved(true); setTimeout(()=>setHeroSaved(false), 2000)
  }

  async function uploadHeroPhoto(e) {
    const file = e.target.files?.[0]; if (!file) return
    setUploadingHero(true)
    try {
      const compressed = await compressImage(file, { maxWidth: 2000, maxHeight: 2000, quality: 0.82 })
      const ext  = (compressed.name.split('.').pop() || 'webp')
      const path = `${wedding.id}/hero-${Date.now()}.${ext}`
      const { error } = await supabase.storage.from('wedding-photos').upload(path, compressed)
      if (!error) {
        const { data } = supabase.storage.from('wedding-photos').getPublicUrl(path)
        await supabase.from('weddings').update({ hero_preset: 'photo', hero_image_url: data.publicUrl }).eq('id', wedding.id)
        setHeroImageUrl(data.publicUrl); setHeroPreset('photo')
        await refreshWedding()
        setHeroSaved(true); setTimeout(()=>setHeroSaved(false), 2000)
      }
    } catch (_) {}
    setUploadingHero(false)
    e.target.value = ''
  }

  async function toggleHeroCarousel() {
    const next = !heroShowCarousel
    setHeroShowCarousel(next)
    await supabase.from('weddings').update({ hero_show_carousel: next }).eq('id', wedding.id)
    await refreshWedding()
    setHeroSaved(true); setTimeout(()=>setHeroSaved(false), 2000)
  }

  async function saveHeroTagline() {
    await supabase.from('weddings').update({ hero_tagline: heroTagline.trim() || null }).eq('id', wedding.id)
    await refreshWedding()
    setHeroTagSaved(true); setTimeout(()=>setHeroTagSaved(false), 2000)
  }

  async function toggleVisibility(key) {
    const next = {...visibility,[key]:!visibility[key]}
    setVisibility(next)
    await supabase.from('weddings').update({portal_visibility:next}).eq('id',wedding.id)
  }

  async function togglePasswordProtection() {
    const next = !passwordProtected
    setPasswordProtected(next)
    await supabase.from('weddings').update({portal_password_protected:next}).eq('id',wedding.id)
    if(!next) await supabase.from('weddings').update({portal_password:null}).eq('id',wedding.id)
  }

  async function savePortalPassword() {
    if(portalPassword.length<6){ setPasswordErr('Password must be at least 6 characters'); return }
    setPasswordErr('')
    await supabase.from('weddings').update({portal_password:portalPassword,portal_password_protected:true}).eq('id',wedding.id)
    setPasswordSaved(true); setTimeout(()=>setPasswordSaved(false),2000)
  }


  async function saveCurrency() {
    await supabase.from('weddings').update({currency:currencyCode}).eq('id',wedding.id)
    setCurrencySaved(true); setTimeout(()=>setCurrencySaved(false),2000)
  }

  async function sendPartnerInvite() {
    if (!partnerPhone.trim() || !wedding) return
    setInviteSending(true)

    // Insert into partner_invites and get back the generated token
    const { data: invite, error } = await supabase
      .from('partner_invites')
      .insert({ wedding_id: wedding.id, partner_phone: partnerPhone.trim() })
      .select('token')
      .single()

    if (error || !invite?.token) { setInviteSending(false); return }

    // Build join link
    const base    = import.meta.env.VITE_APP_URL || window.location.origin
    const joinUrl = `${base}/join/${invite.token}`

    // Format phone - normalise Nigerian numbers
    let phone = partnerPhone.replace(/\D/g, '')
    if (phone.startsWith('0'))                       phone = '234' + phone.slice(1)
    else if (!phone.startsWith('234') && phone.length <= 10) phone = '234' + phone

    // Build WhatsApp message
    const p1   = wedding.partner1_name?.split(' ')[0] || 'Your partner'
    const both = wedding.partner2_name
      ? `${wedding.partner1_name} & ${wedding.partner2_name}`
      : wedding.partner1_name
    const msg  = `Hi! 🎊 ${p1} has set up your wedding on Okoiya and is inviting you to co-plan everything together.

Okoiya is where you'll manage your guests, RSVPs, events, Aso Ebi, registry, and more - all in one place.

Tap the link below to join the wedding dashboard:
${joinUrl}

You'll be asked to create a free account (it takes 2 minutes). Once you do, you'll have full access to your wedding as a co-planner.

Welcome to the team 💛
- ${both}`

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank')
    setInviteSent(true)
    setInviteSending(false)
  }

  async function addAmbassador() {
    if (!ambPhone.trim() || !wedding) return
    setAmbSaving(true)
    const pin = genPin()
    const { data } = await supabase.from('asoebi_ambassadors').insert({
      wedding_id: wedding.id,
      name: ambName.trim() || null,
      phone: ambPhone.trim(),
      access_pin: pin,
    }).select().single()
    if (data) {
      setAmbassadors(prev => [...prev, data])
      const base = import.meta.env.VITE_APP_URL || window.location.origin
      const link = `${base}/ambassador/${data.access_token}`
      const msg = `Hi ${ambName||'there'}! You've been invited as an Aso Ebi Ambassador for ${wedding.partner1_name} & ${wedding.partner2_name}'s wedding 💛\n\nTap here to view and manage fabric orders:\n${link}\n\nYour PIN: ${pin}\n\nPlease keep this PIN private.`
      const phone = ambPhone.trim().replace(/[\s\-+()]/g,'').replace(/^0/,'234')
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,'_blank')
      setAmbName(''); setAmbPhone('')
    }
    setAmbSaving(false)
  }

  async function removeAmbassador(id) {
    await supabase.from('asoebi_ambassadors').delete().eq('id',id)
    setAmbassadors(prev => prev.filter(a => a.id !== id))
  }

  async function sendCoordinatorInvite() {
    if(!coordPhone||!wedding) return
    const pin = genPin()
    await supabase.from('weddings').update({
      checkin_attendant_name: coordName || null,
      checkin_attendant_phone: coordPhone,
      checkin_pin: pin,
    }).eq('id', wedding.id)
    await refreshWedding()
    const base = import.meta.env.VITE_APP_URL || window.location.origin
    const link = `${base}/checkin/${wedding.checkin_token}`
    const msg = `Hi ${coordName||'there'}! You've been asked to help with guest check-in at ${wedding.partner1_name} & ${wedding.partner2_name}'s wedding.\n\nOn the wedding day, tap this link:\n${link}\n\nYour PIN: ${pin}\n\nPlease keep this PIN private.`
    const phone = coordPhone.replace(/[\s\-+()]/g,'').replace(/^0/,'234')
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`,'_blank')
    setCoordSent(true)
  }

  async function revokeCoordinator() {
    if(!wedding) return
    // Regenerate token (old link dies) + clear coordinator info
    const { data: newToken } = await supabase.rpc('regenerate_checkin_token', { p_wedding_id: wedding.id })
    await supabase.from('weddings').update({
      checkin_attendant_name: null,
      checkin_attendant_phone: null,
      checkin_pin: null,
    }).eq('id', wedding.id)
    setCoordName(''); setCoordPhone('')
    setCoordSent(false)
    await refreshWedding()
  }

  async function saveNotifPrefs() {
    await supabase.from('weddings').update({notification_prefs:notifPrefs}).eq('id',wedding.id)
    setNotifSaved(true); setTimeout(()=>setNotifSaved(false),2000)
  }

  async function saveStoryFeature() {
    setStorySaving(true)
    await supabase.from('weddings').update({
      story_featured: storyFeatured,
      story_featured_name_mode: storyNameMode,
      story_featured_at: storyFeatured ? new Date().toISOString() : null,
    }).eq('id', wedding.id)
    await refreshWedding()
    setStorySaved(true); setTimeout(() => setStorySaved(false), 3000)
    setStorySaving(false)
  }

  async function deleteAccount() {
    setDeleting(true)
    try {
      const { data:{ session } } = await supabase.auth.getSession()
      const res = await fetch(import.meta.env.VITE_SUPABASE_URL+'/functions/v1/delete-account',{method:'POST',headers:{'Authorization':'Bearer '+session.access_token,'Content-Type':'application/json'}})
      if(!res.ok) throw new Error()
      await signOut(); window.location.href='/'
    } catch(e){ alert('Account deletion failed. Please try again.'); setDeleting(false) }
  }

  function copyLink(){ navigator.clipboard.writeText(weddingUrl); setCopied(true); setTimeout(()=>setCopied(false),2000) }


  // Guest portal visibility tabs (match WeddingMiniSite actual tab IDs)
  const PORTAL_TABS = [
    {key:'story',    label:'Our Story'},
    {key:'events',   label:'Events'},
    {key:'registry', label:'Registry'},
    {key:'photos',   label:'Studio (Photos)'},
    {key:'programme',label:'Programme'},
    {key:'eyes',     label:'Through Your Eyes'},
    {key:'asoebi',   label:'Aso Ebi'},
  ]

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', maxWidth:580 }}>

      {/* Search */}
      <div style={{ position:'relative' }}>
        <Search size={14} color="#BCBCBC" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}/>
        <input
          className="input-field"
          placeholder="Search settings…"
          value={searchQuery}
          onChange={e=>{ setSearchQuery(e.target.value); setShowSearch(!!e.target.value) }}
          onBlur={()=>setTimeout(()=>setShowSearch(false),200)}
          style={{ paddingLeft:'2.25rem', paddingRight: searchQuery?'2.25rem':'0.875rem' }}
        />
        {searchQuery && (
          <button onClick={()=>{ setSearchQuery(''); setShowSearch(false) }} style={{ position:'absolute', right:10, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'#BCBCBC' }}>
            <X size={14}/>
          </button>
        )}
        {showSearch && searchQuery && (() => {
          const q = searchQuery.toLowerCase()
          const matches = SETTINGS_ITEMS.filter(s =>
            s.label.toLowerCase().includes(q) || s.keywords.some(k=>k.includes(q))
          )
          if (!matches.length) return null
          return (
            <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'white', border:'1.5px solid #E5E0D8', borderRadius:'0.875rem', boxShadow:'0 8px 24px rgba(0,0,0,0.1)', zIndex:100, overflow:'hidden' }}>
              {matches.slice(0,6).map(item=>(
                <button key={item.label} onMouseDown={e=>e.preventDefault()} onClick={()=>goToSection(item.sectionId)}
                  style={{ display:'flex', alignItems:'center', justifyContent:'space-between', width:'100%', padding:'0.625rem 1rem', background:'none', border:'none', cursor:'pointer', textAlign:'left', borderBottom:'1px solid #F3F0EA' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#F8F5F2'}
                  onMouseLeave={e=>e.currentTarget.style.background='none'}>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:600, color:'#1A1A1A' }}>{item.label}</span>
                  <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', background:'#F3F0EA', borderRadius:100, padding:'2px 8px', flexShrink:0 }}>{item.section}</span>
                </button>
              ))}
            </div>
          )
        })()}
      </div>

      <div>
        <h1 style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.6rem', color:'#1A1A1A', margin:'0 0 0.2rem', letterSpacing:'-0.03em' }}>Settings</h1>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#9B9B9B', margin:0 }}>Manage your account and wedding preferences.</p>
      </div>

      {/* ── YOUR ACCOUNT ── */}
      <SettingsSection title="Your Account" defaultOpen={false} id="settings-account" forceOpen={activeSectionId==='settings-account'}>
        {/* Plan */}
        {isPremium ? (
          <div style={{ background:'rgba(61,139,94,0.08)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.875rem', padding:'0.875rem 1rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>
            <Crown size={16} color="#3D8B5E"/>
            <div>
              <p style={{ fontFamily:'Gibs, serif', fontSize:'1rem', color:'#1A1A1A', margin:0 }}>Premium Plan</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#3D8B5E', margin:0 }}>You have full access to all Okoiya features.</p>
            </div>
          </div>
        ) : (
          <div>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'0.75rem', marginBottom:'0.875rem' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}><User size={16} color="#6B6B6B"/><span style={{ fontFamily:'Gibs, serif', fontSize:'1rem', color:'#1A1A1A' }}>Free Plan</span></div>
              <button className="btn-primary" onClick={()=>navigate('/pricing')} style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 1rem' }}><Crown size={13}/> Upgrade</button>
            </div>
            <AdvancedToggle label="See what Premium unlocks">
              <div style={{ display:'flex', flexDirection:'column', gap:'0.3rem' }}>
                {[{yes:true,label:'Up to 50 guests'},{yes:true,label:'Basic registry'},{yes:false,label:'Group gifting'},{yes:false,label:'Aso Ebi hub'},{yes:false,label:'QR check-in'},{yes:false,label:'Unlimited guests'}].map(f=>(
                  <div key={f.label} style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                    {f.yes?<Check size={12} color="#3D8B5E"/>:<Lock size={11} color="#BCBCBC"/>}
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:f.yes?'#1A1A1A':'#9B9B9B' }}>{f.label}{!f.yes?' — Premium':''}</span>
                  </div>
                ))}
              </div>
            </AdvancedToggle>
          </div>
        )}

        {/* Profile */}
        <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
          <div>
            <label style={lbl}>Full Name</label>
            <input className="input-field" value={fullName}
              onChange={e=>setFullName(e.target.value)}
              onBlur={e=>{ const v=autoCapitalize(e.target.value); setFullName(v); if(isAllCaps(v)) setNameErr('Please use standard capitalization'); else setNameErr('') }}/>
            {nameErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#DC2626', margin:'0.25rem 0 0' }}>{nameErr}</p>}
          </div>
          <div>
            <label style={lbl}>Email Address</label>
            <input className="input-field" value={user?.email||''} readOnly style={{ background:'#F8F5F2', color:'#9B9B9B' }}/>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#8C7B70', margin:'0.25rem 0 0', lineHeight:1.5 }}>
              Email cannot be changed after signup. Need help? <a href="mailto:support@okoiya.com" style={{ color:'#B36B55' }}>support@okoiya.com</a>
            </p>
          </div>
          <button onClick={saveProfile} disabled={savingProfile} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
            {profileSaved?<><Check size={13}/>Saved!</>:savingProfile?'Saving…':'Save Changes'}
          </button>
        </div>
      </SettingsSection>

      {/* ── YOUR WEDDING ── */}
      <SettingsSection title="Your Wedding" defaultOpen={false} id="settings-wedding" forceOpen={activeSectionId==='settings-wedding'}>
        {/* Wedding link */}
        <div>
          <label style={lbl}>Your Wedding Link</label>
          {isLocalhost && (
            <div style={{ background:'rgba(217,119,6,0.08)', border:'1px solid rgba(217,119,6,0.25)', borderRadius:'0.75rem', padding:'0.625rem 0.875rem', marginBottom:'0.625rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#B45309', margin:0 }}>⚠ Your wedding link is not live yet. Deploy to generate a real shareable link.</p>
            </div>
          )}
          <div style={{ background:'#F8F5F2', borderRadius:'0.75rem', padding:'0.625rem 0.875rem', fontFamily:'monospace', fontSize:'0.72rem', color:'#1A1A1A', marginBottom:'0.625rem', wordBreak:'break-all' }}>{weddingUrl}</div>
          <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
            <button onClick={copyLink} style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:copied?'#3D8B5E':'#6B6B6B', background:'white', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.4rem 0.75rem', cursor:'pointer' }}>
              {copied?<Check size={11}/>:<Copy size={11}/>} {copied?'Copied':'Copy'}
            </button>
            <a href={weddingUrl} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#6B6B6B', background:'white', border:'1.5px solid #E5E0D8', borderRadius:100, padding:'0.4rem 0.75rem', textDecoration:'none' }}>
              <ExternalLink size={11}/> Open
            </a>
            <button onClick={()=>{ const msg=`You're invited to our wedding! View all details here: ${weddingUrl}`; window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`,'_blank') }}
              style={{ display:'flex', alignItems:'center', gap:'0.3rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#1A7A3A', background:'rgba(37,211,102,0.08)', border:'1.5px solid rgba(37,211,102,0.25)', borderRadius:100, padding:'0.4rem 0.75rem', cursor:'pointer' }}>
              💬 Share
            </button>
  
          </div>
          <AdvancedToggle label="Customize your link">
            <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginTop:'0.25rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', flexShrink:0 }}>okoiya.com/w/</span>
              <input className="input-field" value={editSlug} onChange={e=>setEditSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,''))} style={{ flex:1, fontSize:'0.8rem' }}/>
              <button onClick={saveSlug} disabled={slugSaving} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'white', background:'#B36B55', border:'none', borderRadius:100, padding:'0.4rem 0.75rem', cursor:'pointer', flexShrink:0 }}>
                {slugSaved?<Check size={12}/>:slugSaving?'…':'Save'}
              </button>
            </div>
            {slugError && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#DC2626', margin:'0.25rem 0 0' }}>{slugError}</p>}
          </AdvancedToggle>
        </div>

        {/* Guest portal hero */}
        <div>
          <label style={lbl}>Guest Portal Hero</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.75rem', lineHeight:1.5 }}>
            The backdrop guests see at the top of your wedding page. Pick a theme, or upload your own photo.
            {heroSaved && <span style={{ color:'#3D8B5E', fontWeight:700 }}> · Saved ✓</span>}
          </p>

          {/* Live preview */}
          {(() => {
            const isPhoto = heroPreset==='photo' && heroImageUrl
            const presetObj = SETTINGS_HERO_PRESETS.find(p=>p.key===heroPreset)
            const hp1 = wedding?.partner1_name || 'Partner 1'
            const hp2 = wedding?.partner2_name || 'Partner 2'
            return (
              <div style={{ position:'relative', height:128, borderRadius:'0.875rem', overflow:'hidden', marginBottom:'0.875rem', border:'1px solid #E5E0D8',
                background:'#FAF6F0',
                backgroundImage: !isPhoto ? (heroPreset==='floral' ? 'linear-gradient(135deg,#F7E4DB,#ECCDBE,#F2DACE)' : presetObj?.swatch) : undefined,
                backgroundSize:'cover', display:'flex', alignItems:'center', justifyContent:'center' }}>
                {(isPhoto || heroPreset==='floral') && (
                  <img src={isPhoto ? heroImageUrl : '/hero-floral.webp'} alt="" onError={e=>{e.currentTarget.style.display='none'}}
                    style={{ position:'absolute', top:0, left:0, width:'100%', height:'100%', objectFit:'cover' }}/>
                )}
                {isPhoto && <div style={{ position:'absolute', top:0, left:0, right:0, bottom:0, background:'linear-gradient(180deg, rgba(20,12,6,0.45), rgba(20,12,6,0.62))' }}/>}
                <div style={{ position:'relative', textAlign:'center', padding:'0 1rem' }}>
                  <p style={{ fontFamily:'Gibs, serif', fontWeight:400, fontSize:'1.25rem', lineHeight:1.2, margin:0, color:isPhoto?'#fff':'#1A1A1A', textShadow:isPhoto?'0 1px 10px rgba(0,0,0,0.4)':'none' }}>
                    {hp1} <span style={{ color:isPhoto?'#F2CBB9':'#C4826A', fontStyle:'italic' }}>&amp;</span> {hp2}
                  </p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.56rem', fontWeight:700, letterSpacing:'0.14em', textTransform:'uppercase', margin:'0.3rem 0 0', color:isPhoto?'rgba(255,255,255,0.75)':'rgba(28,20,16,0.4)' }}>
                    Live preview
                  </p>
                </div>
              </div>
            )
          })()}

          {/* Preset chips */}
          <div style={{ display:'flex', flexWrap:'wrap', gap:'0.5rem', marginBottom:'0.875rem' }}>
            {SETTINGS_HERO_PRESETS.map(pr => {
              const active = heroPreset === pr.key
              return (
                <button key={pr.key} onClick={()=>selectHeroPreset(pr.key)}
                  style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:active?700:600,
                    color: active?'#C4826A':'#6B6B6B', background:'white', cursor:'pointer',
                    border:`1.5px solid ${active?'#C4826A':'#E5E0D8'}`, borderRadius:100, padding:'0.35rem 0.7rem 0.35rem 0.4rem' }}>
                  <span style={{ width:18, height:18, borderRadius:'50%', background:pr.swatch, flexShrink:0, border:'1px solid rgba(0,0,0,0.06)' }}/>
                  {pr.label}{active && <Check size={12}/>}
                </button>
              )
            })}
          </div>

          {/* Upload your own — premium */}
          {isPremium ? (
            <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
              <label style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:700,
                color: heroPreset==='photo'?'#C4826A':'#6B6B6B', background:'white', cursor: uploadingHero?'wait':'pointer',
                border:`1.5px solid ${heroPreset==='photo'?'#C4826A':'#E5E0D8'}`, borderRadius:100, padding:'0.45rem 0.9rem' }}>
                {uploadingHero ? 'Uploading…' : (heroPreset==='photo' ? '✓ Using your photo — replace' : '⬆ Upload your own photo')}
                <input type="file" accept="image/*" style={{ display:'none' }} onChange={uploadHeroPhoto} disabled={uploadingHero}/>
              </label>
              {heroPreset==='photo' && heroImageUrl && (
                <img src={heroImageUrl} alt="" style={{ width:56, height:36, objectFit:'cover', borderRadius:'0.4rem', border:'1px solid #E5E0D8' }}/>
              )}
            </div>
          ) : (
            <button onClick={()=>navigate('/pricing')} style={{ display:'inline-flex', alignItems:'center', gap:'0.4rem', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.74rem', fontWeight:700, color:'#9B7B4A', background:'rgba(196,130,106,0.07)', border:'1.5px dashed rgba(196,130,106,0.4)', borderRadius:100, padding:'0.45rem 0.9rem', cursor:'pointer' }}>
              <Lock size={12}/> Upload your own photo (Premium)
            </button>
          )}

          {isPremium && heroPreset==='photo' && (
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem', marginTop:'0.875rem' }}>
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.82rem', color:'#1A1A1A', margin:0 }}>Show photo carousel</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:0, lineHeight:1.4 }}>Keep the scrolling studio photos over your hero image</p>
              </div>
              <Toggle on={heroShowCarousel} onToggle={toggleHeroCarousel}/>
            </div>
          )}

          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#BCBCBC', margin:'0.5rem 0 0', lineHeight:1.5 }}>
            For best results use a landscape photo, at least 1600px wide, with a calm middle area. We add a soft overlay so your names stay readable.
          </p>

          {/* Hero tagline */}
          <div style={{ marginTop:'1.25rem', paddingTop:'1.25rem', borderTop:'1px solid #F3F0EA' }}>
            <label style={lbl}>Hero Tagline</label>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:'0 0 0.6rem', lineHeight:1.5 }}>
              The small cursive line beside the logo on your portal. Leave blank to use the default, “Where perfect weddings come together”.
            </p>
            <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
              <input className="input-field" value={heroTagline} maxLength={70}
                onChange={e=>setHeroTagline(e.target.value)}
                placeholder="Where perfect weddings come together"
                style={{ flex:'1 1 220px', fontSize:'0.85rem' }}/>
              <button onClick={saveHeroTagline} className="btn-primary" style={{ flexShrink:0, fontSize:'0.8rem', padding:'0.5rem 1.1rem', display:'flex', alignItems:'center', gap:'0.35rem' }}>
                {heroTagSaved ? <><Check size={13}/>Saved!</> : 'Save'}
              </button>
            </div>
            {heroTagline && <p style={{ fontFamily:'Great Vibes, cursive', fontSize:'1.35rem', color:'#C4826A', margin:'0.6rem 0 0' }}>{heroTagline}</p>}
          </div>
        </div>

        {/* Guest portal visibility */}
        <div>
          <label style={lbl}>Guest Portal Tabs</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.75rem', lineHeight:1.5 }}>Choose which tabs appear on your wedding portal. Hidden tabs disappear completely — guests won't see them at all.</p>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
            {PORTAL_TABS.map(tab=>(
              <div key={tab.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', color:'#1A1A1A' }}>{tab.label}</span>
                <Toggle on={visibility[tab.key]!==false} onToggle={()=>toggleVisibility(tab.key)}/>
              </div>
            ))}
          </div>

          {/* Password protection — advanced */}
          <AdvancedToggle label="Password protect your portal">
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.5rem' }}>
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:600, fontSize:'0.82rem', color:'#1A1A1A', margin:0 }}>Require a password to view your portal</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#9B9B9B', margin:0 }}>Guests must enter a password before seeing anything</p>
              </div>
              <Toggle on={passwordProtected} onToggle={togglePasswordProtection}/>
            </div>
            {passwordProtected && (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
                <input className="input-field" type="text" placeholder="Set portal password (min 6 characters)" value={portalPassword} onChange={e=>setPortalPassword(e.target.value)}/>
                {passwordErr && <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#DC2626', margin:0 }}>{passwordErr}</p>}
                <button onClick={savePortalPassword} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.78rem' }}>
                  {passwordSaved?<><Check size={13}/>Password Saved!</>:'Save Password'}
                </button>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:0 }}>Share this password only with your invited guests.</p>
              </div>
            )}
          </AdvancedToggle>
        </div>

        {/* Wedding Date */}
        <div>
          <label style={lbl}>Wedding Date</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.625rem' }}>Updates your countdown, overview bar, and guest portal automatically.</p>
          <div style={{ display:'flex', gap:'0.625rem', flexWrap:'wrap' }}>
            <input type="date" value={weddingDate} onChange={e=>setWeddingDate(e.target.value)} className="input-field" style={{ flex:'1 1 160px' }}/>
            <button onClick={saveWeddingDate} disabled={!weddingDate||savingDate} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
              {dateSaved?<><Check size={13}/>Saved!</>:savingDate?'Saving…':'Save Date'}
            </button>
          </div>
        </div>
      </SettingsSection>

      {/* ── CURRENCY ── */}
      <SettingsSection title="Currency" defaultOpen={false} id="settings-currency" forceOpen={activeSectionId==='settings-currency'}>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#9B9B9B', margin:'0 0 0.75rem' }}>Choose the currency displayed across your budget and payments.</p>
        <div style={{ display:'flex', gap:'0.625rem', flexWrap:'wrap', alignItems:'center' }}>
          <select value={currencyCode} onChange={e=>setCurrencyCode(e.target.value)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', padding:'0.55rem 0.875rem', border:'1.5px solid #E5E0D8', borderRadius:'0.75rem', background:'white', outline:'none', flex:'1 1 160px' }}>
            {[{code:'NGN',label:'₦ NGN — Nigerian Naira'},{code:'USD',label:'$ USD — US Dollar'},{code:'GBP',label:'£ GBP — British Pound'},{code:'EUR',label:'€ EUR — Euro'},{code:'GHS',label:'₵ GHS — Ghanaian Cedi'},{code:'KES',label:'KSh KES — Kenyan Shilling'},{code:'ZAR',label:'R ZAR — South African Rand'}].map(cur=><option key={cur.code} value={cur.code}>{cur.label}</option>)}
          </select>
          <button onClick={saveCurrency} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', flexShrink:0 }}>
            {currencySaved?<><Check size={13}/>Saved!</>:'Save'}
          </button>
        </div>
      </SettingsSection>


      {/* ── PEOPLE & ACCESS ── */}
      <SettingsSection title="People & Access" defaultOpen={false} id="settings-people" forceOpen={activeSectionId==='settings-people'}>
        {/* Partner */}
        <div>
          <label style={lbl}>Your Partner</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.625rem', lineHeight:1.5 }}>Your partner gets full co-access to the same wedding dashboard.</p>
          {(() => {
            // Determine connection state from the live wedding object
            const isConnected  = !!wedding?.partner2_id
            const iAmPartner1  = user?.id === wedding?.partner1_id
            const partnerName  = iAmPartner1
              ? (wedding?.partner2_name || 'Your partner')
              : (wedding?.partner1_name || 'Your partner')

            // CONNECTED — show badge with partner's name
            if (isConnected) {
              return (
                <div style={{ display:'flex', alignItems:'center', gap:'0.625rem', background:'rgba(155,175,155,0.1)', border:'1px solid rgba(155,175,155,0.3)', borderRadius:'0.875rem', padding:'0.75rem 1rem' }}>
                  <div style={{ width:32, height:32, borderRadius:'50%', background:'rgba(155,175,155,0.25)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    <Check size={15} color="#3D8B5E"/>
                  </div>
                  <div>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', fontWeight:700, color:'#3D8B5E', margin:0 }}>Connected</p>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0 }}>
                      {partnerName} is co-managing this wedding with you
                    </p>
                  </div>
                </div>
              )
            }

            // INVITE SENT (session state — before page reload)
            if (inviteSent) {
              return (
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', background:'rgba(61,139,94,0.08)', borderRadius:'0.75rem', padding:'0.75rem' }}>
                  <Check size={14} color="#3D8B5E"/>
                  <div>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#3D8B5E', fontWeight:600, margin:0 }}>WhatsApp opened with your invite!</p>
                    <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0 }}>
                      Need to resend?{' '}
                      <button onClick={()=>{ setInviteSent(false); setPartnerPhone('') }} style={{ background:'none', border:'none', color:'#C4826A', cursor:'pointer', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, padding:0 }}>
                        Send again
                      </button>
                    </p>
                  </div>
                </div>
              )
            }

            // NOT CONNECTED — show invite UI
            return (
              <div style={{ display:'flex', gap:'0.5rem' }}>
                <input className="input-field" type="tel" placeholder="Partner's WhatsApp number (e.g. 08012345678)" value={partnerPhone} onChange={e=>setPartnerPhone(e.target.value)} style={{ flex:1 }}/>
                <button onClick={sendPartnerInvite} disabled={!partnerPhone.trim() || inviteSending} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', flexShrink:0, fontSize:'0.78rem' }}>
                  <Send size={13}/> {inviteSending ? '...' : 'Invite'}
                </button>
              </div>
            )
          })()}
        </div>

        {/* Coordinator — Premium only */}
        <div id="coordinator">
          <label style={lbl}>Check-In Coordinator</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.625rem', lineHeight:1.5 }}>Give a trusted person check-in access without sharing your login.</p>
          {!isPremium ? (
            <div style={{ background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.75rem', padding:'0.875rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.5rem' }}>This is a premium feature. Upgrade to delegate check-in to a coordinator.</p>
              <button onClick={()=>navigate('/pricing')} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 1rem' }}><Crown size={13}/> Upgrade to Premium</button>
            </div>
          ) : null}
          {isPremium && (
            coordSent || wedding?.checkin_attendant_phone ? (
            <div style={{ background:'rgba(61,139,94,0.08)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.75rem', padding:'0.875rem', display:'flex', flexDirection:'column', gap:'0.3rem' }}>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#3D8B5E', margin:0 }}>✓ {coordName||wedding.checkin_attendant_name||'Coordinator'} has check-in access</p>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:0 }}>{coordPhone||wedding.checkin_attendant_phone}</p>
              <div style={{ display:'flex', gap:'0.5rem', marginTop:'0.35rem' }}>
                <button onClick={()=>{ setCoordSent(false) }} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#B36B55', background:'none', border:'none', cursor:'pointer', padding:0 }}>Change →</button>
                <button onClick={revokeCoordinator} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#DC2626', background:'none', border:'none', cursor:'pointer', padding:0 }}>Revoke Access</button>
              </div>
            </div>
          ) : (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
              <input className="input-field" placeholder="Coordinator name (optional)" value={coordName} onChange={e=>setCoordName(e.target.value)}/>
              <input className="input-field" placeholder="Coordinator phone number" value={coordPhone} onChange={e=>setCoordPhone(e.target.value)}/>
              <button onClick={sendCoordinatorInvite} disabled={!coordPhone} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.82rem' }}>
                <Send size={13}/> Send Access Link via WhatsApp
              </button>
              <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:0, lineHeight:1.5 }}>
                They will receive a secure link — no account or login needed.
              </p>
            </div>
          )
        )}
        </div>

        {/* Aso Ebi Ambassadors */}
        <div>
          <label style={lbl}>Aso Ebi Ambassadors</label>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#9B9B9B', margin:'0 0 0.75rem', lineHeight:1.5 }}>
            Ambassadors help coordinate fabric orders from guests in their area. Add their phone number below — they'll receive a secure link via WhatsApp.
          </p>
          {ambassadors.length > 0 && (
            <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', marginBottom:'0.75rem' }}>
              {ambassadors.map(a=>(
                <div key={a.id} style={{ background:'rgba(61,139,94,0.08)', border:'1px solid rgba(61,139,94,0.2)', borderRadius:'0.75rem', padding:'0.875rem', display:'flex', flexDirection:'column', gap:'0.3rem' }}>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#3D8B5E', margin:0 }}>✓ {a.name||'Ambassador'} has access</p>
                  <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.75rem', color:'#6B6B6B', margin:0 }}>{a.phone || a.email}</p>
                  <button onClick={()=>removeAmbassador(a.id)} style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:600, color:'#DC2626', background:'none', border:'none', cursor:'pointer', padding:0, textAlign:'left', marginTop:'0.1rem' }}>Revoke Access</button>
                </div>
              ))}
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem' }}>
            <input className="input-field" placeholder="Ambassador name (optional)" value={ambName} onChange={e=>setAmbName(e.target.value)}/>
            <input className="input-field" placeholder="Ambassador phone number *" value={ambPhone} onChange={e=>setAmbPhone(e.target.value)}/>
            <button onClick={addAmbassador} disabled={!ambPhone.trim()||ambSaving} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.82rem' }}>
              <Send size={13}/> {ambSaving ? 'Adding…' : 'Add & Invite via WhatsApp'}
            </button>
          </div>
          <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.68rem', color:'#9B9B9B', margin:'0.5rem 0 0', lineHeight:1.5 }}>
            They will receive a secure link — no account or login needed.
          </p>
        </div>
      </SettingsSection>

      {/* ── NOTIFICATIONS ── */}
      <SettingsSection title="Notifications" defaultOpen={false} id="settings-notifs" forceOpen={activeSectionId==='settings-notifs'}>
        <div style={{ display:'flex', flexDirection:'column', gap:'0.875rem' }}>
          <div style={{ background:'rgba(196,130,106,0.06)', border:'1px solid rgba(196,130,106,0.15)', borderRadius:'0.75rem', padding:'0.625rem 0.875rem', display:'flex', alignItems:'flex-start', gap:'0.5rem' }}>
            <span style={{ fontSize:'0.85rem', flexShrink:0, marginTop:'0.05rem' }}>🔔</span>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', color:'#6B6B6B', margin:0, lineHeight:1.55 }}>
              Delivered as push notifications. Make sure you allow notifications for Okoiya in your browser or home screen app.
            </p>
          </div>
          {[
            {key:'rsvp',      label:'RSVP Updates',          desc:'When a guest confirms or declines'},
            {key:'checkin',   label:'Guest Arrivals',        desc:'When a guest checks in on the day'},
            {key:'asoebi',    label:'Aso Ebi Orders',        desc:'When a guest places a fabric order'},
            {key:'registry',  label:'Registry Activity',     desc:'When a gift is reserved or contributed to'},
            {key:'reminders', label:'Daily Countdown Reminder', desc:'A gentle daily push with your countdown — and your weekly question'},
          ].map(n=>(
            <div key={n.key} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontWeight:700, fontSize:'0.82rem', color:'#1A1A1A', margin:0 }}>{n.label}</p>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.7rem', color:'#9B9B9B', margin:0 }}>{n.desc}</p>
              </div>
              <Toggle on={notifPrefs[n.key]?.on !== false} onToggle={()=>setNotifPrefs(p=>({...p,[n.key]:{on:!(p[n.key]?.on !== false)}}))}/>
            </div>
          ))}
          <button onClick={saveNotifPrefs} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
            {notifSaved?<><Check size={13}/>Saved!</>:'Save Preferences'}
          </button>
        </div>
      </SettingsSection>

      {/* ── YOUR STORY ── */}
      <SettingsSection title="Your Story" defaultOpen={false} id="settings-story" forceOpen={activeSectionId==='settings-story'}>
        <style>{`
          @keyframes storyFadeIn { from { opacity:0; max-height:0 } to { opacity:1; max-height:300px } }
        `}</style>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:0, lineHeight:1.75 }}>
          You poured your heart into your Our Story section — the proposal, how you met, the words you
          wrote for each other. With your permission, we would love to feature your story on our website,
          so other couples planning their own weddings can be inspired by yours.
        </p>
        <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#6B6B6B', margin:'0.5rem 0 0', lineHeight:1.75 }}>
          Your studio photos would be included. You choose whether your names appear or stay anonymous.
          You can change your mind any time.
        </p>

        {!isPremium ? (
          <div style={{ background:'rgba(196,130,106,0.08)', border:'1px solid rgba(196,130,106,0.2)', borderRadius:'0.75rem', padding:'0.875rem 1rem' }}>
            <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.78rem', color:'#6B6B6B', margin:'0 0 0.5rem', lineHeight:1.6 }}>
              Featuring your story on Okoiya is a Premium feature. Upgrade to unlock this and give your love story a stage.
            </p>
            <button onClick={()=>navigate('/pricing')} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.35rem', fontSize:'0.78rem', padding:'0.5rem 1rem' }}>
              <Crown size={13}/> Upgrade to Premium
            </button>
          </div>
        ) : (
          <>
            {/* Toggle */}
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:'0.25rem' }}>
              <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:600, color:'#1A1A1A' }}>
                Allow Okoiya to feature our story
              </span>
              <button onClick={() => setStoryFeatured(v => !v)}
                style={{ width:44, height:24, borderRadius:100, background:storyFeatured?'#C4826A':'#E5E0D8', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
                <div style={{ width:18, height:18, borderRadius:'50%', background:'white', position:'absolute', top:3, left:storyFeatured?23:3, transition:'left 0.2s ease', boxShadow:'0 1px 3px rgba(0,0,0,0.18)' }}/>
              </button>
            </div>

            {/* Name mode selector — fades in when toggle is ON */}
            {storyFeatured && (
              <div style={{ overflow:'hidden', animation:'storyFadeIn 0.25s ease-out', display:'flex', flexDirection:'column', gap:'0.625rem', paddingLeft:'0.125rem' }}>
                <p style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.72rem', fontWeight:700, color:'#8C7B70', textTransform:'uppercase', letterSpacing:'0.08em', margin:0 }}>How should we display your names?</p>
                {[
                  { value:'real',      label:`With our real names — "${[wedding.partner1_name, wedding.partner2_name].filter(Boolean).join(' & ')}"` },
                  { value:'anonymous', label:`Keep us anonymous — "A couple from ${wedding.city || 'Nigeria'}"` },
                ].map(opt => (
                  <label key={opt.value} style={{ display:'flex', alignItems:'flex-start', gap:'0.625rem', cursor:'pointer' }}>
                    <input
                      type="radio"
                      name="storyNameMode"
                      value={opt.value}
                      checked={storyNameMode === opt.value}
                      onChange={() => setStoryNameMode(opt.value)}
                      style={{ marginTop:'0.2rem', accentColor:'#C4826A', flexShrink:0 }}
                    />
                    <span style={{ fontFamily:'Nunito Sans, sans-serif', fontSize:'0.82rem', color:'#1A1A1A', lineHeight:1.55 }}>{opt.label}</span>
                  </label>
                ))}
              </div>
            )}

            <button onClick={saveStoryFeature} disabled={storySaving} className="btn-primary" style={{ display:'flex', alignItems:'center', gap:'0.4rem', alignSelf:'flex-start' }}>
              {storySaved ? <><Check size={13}/>Your preference has been saved. Thank you ✨</> : storySaving ? 'Saving…' : 'Save preference'}
            </button>
          </>
        )}
      </SettingsSection>

      {/* ── BOTTOM ACTIONS ── */}
      <div id="settings-actions" style={{ borderTop:'1px solid #E5E0D8', paddingTop:'1.25rem', display:'flex', flexDirection:'column', gap:'0.625rem', marginTop:'0.25rem' }}>
        <button onClick={async()=>{ await signOut(); navigate('/') }}
          style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.85rem', fontWeight:600, color:'#6B6B6B', background:'white', border:'1.5px solid #D1C9C0', borderRadius:100, padding:'0.75rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}>
          <LogOut size={14}/> Sign Out
        </button>
        <button onClick={()=>setShowDelete(true)}
          style={{ width:'100%', fontFamily:'Nunito Sans, sans-serif', fontSize:'0.8rem', fontWeight:600, color:'rgba(179,107,85,0.6)', background:'transparent', border:'1.5px solid rgba(179,107,85,0.3)', borderRadius:100, padding:'0.65rem', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}>
          <Trash2 size={13}/> Delete Account & All Data
        </button>
      </div>

      {showDelete && <DeleteModal onClose={()=>setShowDelete(false)} onDelete={deleteAccount} deleting={deleting}/>}
    </div>
  )
}
